import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * Listens for variant updates and replaces the displayed price
 * with the real price returned by Shopify for the selected variant.
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    closestSection.addEventListener(
      ThemeEvents.variantUpdate,
      this.updatePrice
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    closestSection.removeEventListener(
      ThemeEvents.variantUpdate,
      this.updatePrice
    );
  }

  /**
   * Updates the price and volume pricing note.
   *
   * @param {VariantUpdateEvent} event
   */
  updatePrice = (event) => {
    const data = event.detail?.data;

    if (!data?.html) return;

    /*
     * A product card swatch can change the entire product.
     * A normal product-page variant update keeps the same product.
     */
    const updatedProductId = String(
      data.newProduct?.id ?? data.productId ?? ''
    );

    if (!updatedProductId) return;

    if (data.newProduct) {
      this.dataset.productId = updatedProductId;
    } else if (
      updatedProductId !== String(this.dataset.productId)
    ) {
      return;
    }

    /*
     * Find the price belonging to this exact product and block.
     * This prevents another product-price element, for example
     * from the bundle, from being used accidentally.
     */
    const newProductPrice = data.html.querySelector(
      `product-price[data-product-id="${updatedProductId}"][data-block-id="${this.dataset.blockId}"]`
    );

    if (!newProductPrice) {
      console.warn('Product price was not found in updated variant HTML.', {
        productId: updatedProductId,
        blockId: this.dataset.blockId,
      });

      return;
    }

    /*
     * Always find the current elements directly in the DOM.
     * Previously stored refs may point to detached elements
     * after the first price replacement.
     */
    const currentPriceContainer = this.querySelector(
      '[ref="priceContainer"]'
    );

    const newPriceContainer = newProductPrice.querySelector(
      '[ref="priceContainer"]'
    );

    if (currentPriceContainer && newPriceContainer) {
      currentPriceContainer.replaceWith(
        newPriceContainer.cloneNode(true)
      );
    }

    /*
     * Update or remove the volume pricing note.
     */
    const currentVolumePricingNote = this.querySelector(
      '[ref="volumePricingNote"]'
    );

    const newVolumePricingNote = newProductPrice.querySelector(
      '[ref="volumePricingNote"]'
    );

    if (!newVolumePricingNote) {
      currentVolumePricingNote?.remove();
    } else if (currentVolumePricingNote) {
      currentVolumePricingNote.replaceWith(
        newVolumePricingNote.cloneNode(true)
      );
    } else {
      const updatedPriceContainer = this.querySelector(
        '[ref="priceContainer"]'
      );

      updatedPriceContainer?.insertAdjacentElement(
        'afterend',
        /** @type {Element} */ (
          newVolumePricingNote.cloneNode(true)
        )
      );
    }

    /*
     * Update Shopify payment installments with the selected variant ID.
     */
    const installmentsSelector =
      `#product-form-installment-${this.dataset.blockId} input[name="id"]`;

    const installmentsInput =
      /** @type {HTMLInputElement|null} */ (
        this.querySelector(installmentsSelector)
      );

    if (installmentsInput) {
      installmentsInput.value = String(
        event.detail.resource?.id ?? ''
      );

      installmentsInput.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}